import axios from "@/utils/axiosinstance";
import defaultAvatar from "../components/icons/Avatar.svg";

// ── In-memory profile image cache ──────────────────────────────────────────
// Keyed by userID. Stores the resolved object URL (or defaultAvatar string).
// In-flight requests are stored as Promises so concurrent callers for the
// same userID share a single HTTP request instead of racing.
const _profileImageCache = new Map();

// ── In-memory verification status cache ───────────────────────────────────
// Same dedup pattern: stores Promises so concurrent card renders for the
// same userID share a single HTTP request.
const _verificationCache = new Map();

export default {
  getUserDetails(payload) {
    return axios.post("/api/userdetailslist", payload);
  },

  getUserInfo(userID) {
    return axios.post("/api/getoneusers", { userID });
  },

  getVerificationStatus(userID) {
    if (_verificationCache.has(userID)) {
      return _verificationCache.get(userID);
    }
    const promise = axios.post("/api/user/verification-status", { userID });
    _verificationCache.set(userID, promise);
    // Clear cache entry after resolution so future page loads get fresh data
    promise.finally(() => {
      setTimeout(() => _verificationCache.delete(userID), 60000);
    });
    return promise;
  },

  async fetchUnreadCounts(userId) {
    if (!userId) {
      return 0;
    }
    return axios.post("/api/messagelist", {
      toUserId: userId,
      isRead: false,
    });
  },

  async getProfileImage(userID) {
    // Return cached result immediately (resolved URL or defaultAvatar)
    if (_profileImageCache.has(userID)) {
      return _profileImageCache.get(userID);
    }

    // Store the Promise so parallel callers for the same userID share it
    const fetchPromise = (async () => {
      try {
        const imgListRes = await axios.post("/api/imagelist", { userID });
        if (!imgListRes.data.items || imgListRes.data.items.length === 0) {
          return defaultAvatar;
        }
        const fileName = imgListRes.data.items[0].filepath;
        const fileRes = await axios.post(
          "/api/file/get",
          { fileName, albumName: "default" },
          { responseType: "blob" }
        );
        if (!fileRes.data || fileRes.data.size === 0) {
          return defaultAvatar;
        }
        const contentType = fileRes.data.type || fileRes.headers["content-type"];
        if (!contentType || !contentType.startsWith("image/")) {
          return defaultAvatar;
        }
        const blob = new Blob([fileRes.data], { type: contentType });
        if (blob.size === 0) return defaultAvatar;
        return URL.createObjectURL(blob);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn("Error fetching profile image:", error);
        }
        return defaultAvatar;
      }
    })();

    // Cache the Promise right away — subsequent calls before it resolves
    // will await the same Promise rather than fire new requests.
    _profileImageCache.set(userID, fetchPromise);

    const result = await fetchPromise;
    // Replace the Promise with the resolved value for instant future lookups
    _profileImageCache.set(userID, result);
    return result;
  },

  /** Call this on logout to free object URLs and clear the cache. */
  clearProfileImageCache() {
    for (const value of _profileImageCache.values()) {
      if (typeof value === "string" && value.startsWith("blob:")) {
        URL.revokeObjectURL(value);
      }
    }
    _profileImageCache.clear();
  },
};
